import { type InfiniteData, type QueryFunctionContext, useInfiniteQuery } from '@tanstack/react-query';
import * as qs from 'qs';

import type { ISelectOption } from '@model/select';
import type { ITableSorting } from '@model/tableSorting';

import { apiClient } from '@services/apiClient';

import {
  type IArchivedAiInfiniteList,
  type ICostAvoidedRange,
  type ITimeCreatedReportRange,
  type ITimeUpdatedRange,
  type IUseArchivedAiInfiniteListKeys,
} from '../model/archivedAi';

export const archivedAiKey = (
  keys: IUseArchivedAiInfiniteListKeys,
): [
  string,
  ITableSorting,
  Array<number>,
  string,
  ISelectOption,
  ITimeUpdatedRange,
  ICostAvoidedRange,
  ISelectOption,
  ITimeCreatedReportRange,
] => [
  'archivedAiList',
  keys?.sorting,
  keys?.locationIds,
  keys?.searchValue,
  keys?.causeCategory,
  keys?.timeUpdatedRange,
  keys?.costAvoidedRange,
  keys?.type,
  keys?.timeCreatedReportRange,
];

const archivedAiFn = ({
  queryKey,
  pageParam = 1,
}: QueryFunctionContext<ReturnType<typeof archivedAiKey>, number>) => {
  const { minTimeUpdated, maxTimeUpdated } = queryKey[5];
  const { minCostAvoided, maxCostAvoided } = queryKey[6];
  const { minTimeCreated, maxTimeCreated } = queryKey[8];

  const params = {
    location_ids: queryKey[2],
    search: queryKey[3] || undefined,
    filter: {
      cause_category: queryKey[4].value === 'all' ? undefined : queryKey[4].value,
      time_closed_ai_range:
        minTimeUpdated && maxTimeUpdated ? `${minTimeUpdated} - ${maxTimeUpdated}` : undefined,
      time_created_report_range:
        minTimeCreated && maxTimeCreated ? `${minTimeCreated} - ${maxTimeCreated}` : undefined,
      cost_avoided_range:
        minCostAvoided === undefined && maxCostAvoided === undefined
          ? undefined
          : `${minCostAvoided} - ${maxCostAvoided}`,
      type: queryKey[7].value === 'all' ? undefined : queryKey[7].value,
    },
    page: pageParam,
    limit: 100,
    sort: {
      field: queryKey[1].id || 'updatedAt',
      direction: !queryKey[1].id ? 'desc' : queryKey[1].desc ? 'desc' : 'asc',
    },
  };

  return apiClient
    .get<IArchivedAiInfiniteList>(`${APP_API_URL_V2}fsd/action-item/report/archived-action-items`, {
      params,
      paramsSerializer: {
        serialize: (params) => qs.stringify(params),
      },
    })
    .then((data) => data?.data);
};

const archivedAiSelect = (data: InfiniteData<IArchivedAiInfiniteList>) => {
  const archivedAiList = data?.pages?.flatMap((page) => page.data) || [];
  const lastData = data?.pages[data?.pages.length - 1];
  const neighborAisById: {
    [key: string]: { prevId: number | null; nextId: number | null; currentPosition: number };
  } = {};

  archivedAiList.forEach((ai, i) => {
    neighborAisById[ai.id] = {
      prevId: archivedAiList[i - 1]?.id,
      nextId: archivedAiList[i + 1]?.id,
      currentPosition: i + 1,
    };
  });

  return {
    list: archivedAiList,
    pageParams: data.pageParams,
    totalCount: lastData.meta.total,
    neighborAisById,
    stats: lastData.stats,
  };
};

export const useArchivedAiListInfiniteQuery = (keys: IUseArchivedAiInfiniteListKeys) => {
  // @ts-ignore TODO: update react-query to v5
  return useInfiniteQuery({
    queryKey: archivedAiKey(keys),
    queryFn: archivedAiFn,
    enabled: !!keys.enabled,
    select: (data) => archivedAiSelect(data),
    getNextPageParam: ({ meta: { currentPage, lastPage } }) =>
      currentPage < lastPage ? currentPage + 1 : undefined,
  });
};
